<template>
  <SuggestedKeywordsBooleanSelector
    v-model="filter"
    :is-social-stream="isSocialStream"
    :show-keyword="Boolean(filter.type)"
  >
    <template #btn="{ toggle }">
      <div
        class="tw:flex tw:items-center tw:overflow-hidden tw:rounded-3xl tw:border tw:border-solid"
        :class="[tagClass, { 'tw:cursor-pointer': !disabled && allowToggle }]"
        @click="allowToggle && toggle()"
      >
        <div
          v-if="prefix"
          class="tw:flex tw:items-center tw:border-0 tw:border-r tw:border-solid tw:p-2 tw:py-1"
          :class="`${tagClass}-prefix`"
        >
          <span class="tw:font-bold">
            {{ prefix }}
          </span>
        </div>
        <div class="tw:whitespace-nowrap tw:p-2 tw:py-1">
          <span :class="`${tagClass}-operator`">
            {{ tagLabelPrefix }}
          </span>
          <span
            class="tw:whitespace-nowrap"
            :class="{ 'tw:text-blue-700': filter.isSuggestion }"
          >
            {{ tagLabel }}
          </span>
        </div>
        <div
          v-if="!disabled"
          class="tw:flex tw:items-center"
        >
          <div
            class="tw:mr-1 tw:flex tw:cursor-pointer tw:items-center tw:rounded-full tw:p-0.5 tw:text-white"
            :class="`${tagClass}-remove`"
            @click.stop="emit('remove', index)"
          >
            <BaseIcon
              class="tw:text-xs"
              icon="close"
            />
          </div>
        </div>
      </div>
    </template>
  </SuggestedKeywordsBooleanSelector>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon } from "shared/components/base";
import type { SimpleBooleanFilter } from "shared/resources/Stream";
import { SimpleBooleanFilterType } from "shared/resources/Stream";

import SuggestedKeywordsBooleanSelector from "./SuggestedKeywordsBooleanSelector.vue";

const filter = defineModel<SimpleBooleanFilter>({ required: true });

interface SuggestedKeywordsBooleanTagProps {
  index: number;
  mustNot?: boolean;
  should?: boolean;
  pending?: boolean;
  disabled?: boolean;
  isSocialStream?: boolean;
}

const props = defineProps<SuggestedKeywordsBooleanTagProps>();

const emit = defineEmits(["remove"]);

const tagClass = computed(() => {
  if (props.pending) {
    return "pending";
  }

  if (props.mustNot) {
    return "not";
  }

  return "default";
});

const prefix = computed(() => {
  if (props.index < 1 && !props.mustNot) {
    return "";
  }

  if (props.mustNot) {
    return "NOT";
  }

  if (props.should) {
    return "OR";
  }

  return "AND";
});

const tagLabel = computed(() => filter.value.keyword || "");

const tagLabelPrefix = computed(() => {
  if (!filter.value.type) {
    return "";
  }

  let label = "";

  if (filter.value.type === SimpleBooleanFilterType.CASE_SENSITIVE) {
    label = getLocaleText(
      "stream_settings_simple_boolean_tag.case_sensitive_label"
    );
  } else if (filter.value.type === SimpleBooleanFilterType.FIRST) {
    label = getLocaleText("stream_settings_simple_boolean_tag.first_label", {
      value: filter.value.value,
    });
  } else if (filter.value.type === SimpleBooleanFilterType.AT_LEAST) {
    label = getLocaleText("stream_settings_simple_boolean_tag.at_least_label", {
      value: filter.value.value,
    });
  } else if (
    filter.value.type === SimpleBooleanFilterType.MEDIUM &&
    Array.isArray(filter.value.value)
  ) {
    label = getLocaleText("stream_settings_simple_boolean_tag.medium_label", {
      medium: filter.value.value.join(" OR "),
    });
  } else if (
    filter.value.type === SimpleBooleanFilterType.PLATFORM &&
    Array.isArray(filter.value.value)
  ) {
    label = getLocaleText("stream_settings_simple_boolean_tag.platform_label", {
      platform: filter.value.value.join(" OR "),
    });
  } else {
    label = getLocaleText(
      `stream_settings_simple_boolean_tag.${filter.value.type}_label`
    );
  }

  if (filter.value.keyword) {
    label += ":";
  }

  return label;
});

const allowToggle = computed(
  () => Boolean(filter.value.keyword) && !props.disabled
);
</script>

<style scoped>
@reference "shared/css/tailwind.css";

.default {
  @apply tw:border-denim-300 tw:bg-denim-100 tw:text-denim-900 tw:hover:border-denim-500;
}

.default-prefix {
  @apply tw:border-denim-300 tw:bg-denim-200 tw:text-denim-700;
}

.default-operator {
  @apply tw:text-denim-600;
}

.default-remove {
  @apply tw:bg-denim-500 tw:hover:bg-primary;
}

.pending {
  @apply tw:border-orange-300 tw:bg-orange-100 tw:text-orange-900;
}

.pending-prefix {
  @apply tw:border-orange-300 tw:bg-orange-200 tw:text-orange-700;
}

.pending-operator {
  @apply tw:text-orange-600;
}

.pending-remove {
  @apply tw:bg-orange-500 tw:hover:bg-orange-900;
}

.not {
  @apply tw:border-red-300 tw:bg-red-100 tw:text-red-900;
}

.not-prefix {
  @apply tw:border-red-300 tw:bg-red-200 tw:text-red-700;
}

.not-operator {
  @apply tw:text-red-600;
}

.not-remove {
  @apply tw:bg-red-500 tw:hover:bg-red-900;
}
</style>
